import {
  Artifact,
  ArtifactAction,
  ArtifactActions,
  ArtifactContent,
  ArtifactHeader,
  ArtifactTitle,
} from "@/components/ai-elements/artifact";
import { useArtifactPdfDownload } from "@/hooks/useArtifactPdfDownload";
import { useArtifactUrl } from "@/hooks/useArtifactUrl";
import { useTranslation } from "@/hooks/useTranslation";
import { ProducedArtifact } from "@/lib/artifact";
import { chatArtifactPanelStore } from "@/stores/chat-artifact-panel-store";
import {
  ArrowLeftIcon,
  DownloadIcon,
  Loader2Icon,
  PrinterIcon,
  XIcon,
} from "lucide-react";

type ChatArtifactPanelProps = {
  readonly artifact: ProducedArtifact | null;
  /** The conversation the Artifact belongs to: the PDF route is scoped to it. */
  readonly conversationId: string;
  readonly className?: string;
  /**
   * `back` when the panel is laid over the conversation under a host header
   * that already has a close of its own (the company chat's): a back arrow
   * before the title then replaces the panel's cross, so the two do not read
   * as the same action.
   */
  readonly dismissAs?: ArtifactDismiss;
};

type ArtifactDismiss = "close" | "back";

/**
 * The chat's Artifact side panel: title, Download and Print, and the
 * isolated preview itself. The host passes the Artifact to show rather than
 * this reading `chatArtifactPanelStore` itself (D-12: one Artifact at a time,
 * the last one produced or clicked): while the panel closes, the store is
 * already empty, and the host keeps handing it the Artifact on its way out
 * so the exit animation still has something to show (`useExitTransition`).
 *
 * Renders `null` without an Artifact: on desktop the resizable panel that
 * hosts it is collapsed in that case, and on mobile the sheet is closed.
 */
export function ChatArtifactPanel({
  artifact,
  conversationId,
  className,
  dismissAs = "close",
}: ChatArtifactPanelProps) {
  const { download, isDownloading } = useArtifactPdfDownload(conversationId);

  if (!artifact) {
    return null;
  }

  return (
    <ChatArtifactPanelContent
      artifact={artifact}
      className={className}
      conversationId={conversationId}
      dismissAs={dismissAs}
      download={download}
      isDownloading={isDownloading}
    />
  );
}

type ChatArtifactPanelContentProps = {
  readonly artifact: ProducedArtifact;
  readonly conversationId: string;
  readonly className?: string;
  readonly dismissAs: ArtifactDismiss;
  readonly download: (artifact: ProducedArtifact) => Promise<void>;
  readonly isDownloading: boolean;
};

/**
 * The panel itself, once there is an Artifact to show: a component of its
 * own so `useArtifactUrl` is only called with one.
 */
function ChatArtifactPanelContent({
  artifact,
  conversationId,
  className,
  dismissAs,
  download,
  isDownloading,
}: ChatArtifactPanelContentProps) {
  const { t } = useTranslation();
  const { frameUrl, printUrl, isPending } = useArtifactUrl(
    conversationId,
    artifact
  );

  const handlePrint = () => {
    if (!printUrl) {
      return;
    }
    // Never `iframeRef.current.contentWindow.print()`: the iframe is fully
    // sandboxed (artifact.tsx) and a cross-origin `WindowProxy` exposes no
    // `print` to call from here — see D-05 in the PR description. The
    // artifact's own presigned URL opens in a plain new tab instead, where
    // the one script the document may run (`wrapArtifactHtml`, allowed by
    // its hash) prints it once it sees `#print=1` — a fragment, so it never
    // reaches the server and never touches the URL's signature.
    window.open(`${printUrl}#print=1`, "_blank", "noopener,noreferrer");
  };

  return (
    <Artifact className={className}>
      <ArtifactHeader>
        {dismissAs === "back" && (
          <ArtifactAction
            aria-label={t("chat.artifact.backToConversation")}
            onClick={() => chatArtifactPanelStore.close()}
            tooltip={t("chat.artifact.backToConversation")}
          >
            <ArrowLeftIcon className="size-4" />
          </ArtifactAction>
        )}
        <ArtifactTitle>{artifact.title || t("chat.artifact.untitled")}</ArtifactTitle>
        <ArtifactActions>
          <ArtifactAction
            aria-busy={isDownloading}
            aria-label={t("chat.artifact.download")}
            // Not tied to the presigned URL: the PDF route reads the stored
            // page itself, and answers its own 404 or 503 with a toast.
            disabled={isDownloading}
            onClick={() => void download(artifact)}
            tooltip={t("chat.artifact.download")}
          >
            {isDownloading ? (
              <Loader2Icon className="size-4 animate-spin motion-reduce:animate-none" />
            ) : (
              <DownloadIcon className="size-4" />
            )}
          </ArtifactAction>
          <ArtifactAction
            aria-label={t("chat.artifact.print")}
            disabled={!printUrl}
            onClick={handlePrint}
            tooltip={t("chat.artifact.print")}
          >
            <PrinterIcon className="size-4" />
          </ArtifactAction>
          {dismissAs === "close" && (
            <ArtifactAction
              aria-label={t("chat.artifact.close")}
              onClick={() => chatArtifactPanelStore.close()}
              tooltip={t("chat.artifact.close")}
            >
              <XIcon className="size-4" />
            </ArtifactAction>
          )}
        </ArtifactActions>
      </ArtifactHeader>
      <ArtifactContent
        fallback={t("chat.artifact.loadFailed")}
        isPending={isPending}
        src={frameUrl}
      />
    </Artifact>
  );
}
